import React, { useState, useRef, useEffect } from "react";
import { motion } from "framer-motion";
import { Link, useNavigate } from "react-router-dom";
import {
  Menu,
  Users,
} from "lucide-react";


import { CreditBalanceBadge } from "../Spotlight/CreditBalanceBadge";
import { NotificationBell } from "../Notifications/NotificationBell";

import { useThemeStore } from "../../store/theme";
import { useAppStore } from "../../store";
import { useMobileNavigation } from "../../hooks/useMobileNavigation";
import { resolveAvatar } from "@/utils/avatar";

const defaultAvatar = "/assets/avatar.png";

const TopNavigation: React.FC = () => {
  const { colors, theme } = useThemeStore();
  const { user } = useAppStore();
  const navigate = useNavigate();
  const { isMobile, openLeftDrawer, openRightDrawer } = useMobileNavigation();

  const [, setIsUserMenuOpen] = useState(false);
  const [, setIsNotificationsOpen] = useState(false);
  const [, setIsMobileMenuOpen] = useState(false);
  const [, setAvatar] = useState<string>(defaultAvatar);

  const userMenuRef = useRef<HTMLDivElement>(null);
  const notificationsRef = useRef<HTMLDivElement>(null);

  // Close menus when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        userMenuRef.current &&
        !userMenuRef.current.contains(event.target as Node)
      ) {
        setIsUserMenuOpen(false);
      }
      if (
        notificationsRef.current &&
        !notificationsRef.current.contains(event.target as Node)
      ) {
        setIsNotificationsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Close mobile menu when route changes
  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [navigate]);




  useEffect(() => {
    setAvatar(resolveAvatar(user?.avatar));
  }, [user?.avatar]);




  return (
    <motion.header
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.5 }}
      className="fixed top-0 z-50 w-full transition-all duration-200"
      style={{
        backgroundColor: colors.background,
        borderBottom: `1px solid ${theme === "light" ? "#e5e7eb" : "#374151"}`,
      }}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Left Side - Mobile Menu Button */}
          {isMobile && (
            <button
              onClick={openLeftDrawer}
              className="p-2 transition-colors hover:opacity-80"
              style={{ color: colors.text }}
              aria-label="Open menu"
            >
              <Menu size={20} />
            </button>
          )}

          {/* Center - Logo */}
          <div
            className={`${isMobile ? "" : "transform -translate-x-1/2"} no-outline-focus`}
          >
            <Link
              to="/"
              className="flex items-center space-x-2 transition-all duration-200 hover:scale-105 no-outline-focus"
            >
              <img
                src="/vite.png"
                alt="Idearify Logo"
                className="w-8 h-8 rounded-lg object-cover no-outline-focus"
              />
              <span
                className="text-xl font-bold no-outline-focus"
                style={{ color: colors.text }}
              >
                Idearify
              </span>
            </Link>
          </div>

          {/* Right Side - Actions */}
          <div className="flex items-center space-x-2 md:space-x-4 w-full">
            {/* Search - Hidden on mobile */}

            {/* Credit Balance Badge */}
            <CreditBalanceBadge />

            {/* Messages */}
            {/* <button
              className="p-2 transition-colors hover:opacity-80"
              style={{ color: colors.text }}
            >
              <MessageCircle size={20} />
            </button> */}

            {/* Right Sidebar Button - Mobile Only */}
            {isMobile && (
              <button
                onClick={openRightDrawer}
                className="p-2 transition-colors hover:opacity-80"
                style={{ color: colors.text }}
                aria-label="Open suggestions"
              >
                <Users size={20} />
              </button>
            )}

            {/* Notifications */}
            <NotificationBell />

            {/* User Menu */}
            {/* {isLoggedIn && user ? (
              <div className="relative" ref={userMenuRef}>
                <button
                  onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
                  className="flex items-center space-x-2 p-2 rounded-lg transition-all duration-200 hover:scale-105 hover:shadow-md"
                  style={{
                    backgroundColor:
                      theme === "light" ? "#f8fafc" : "transparent",
                    border: `1px solid ${theme === "light" ? "#e2e8f0" : "transparent"}`,
                  }}
                >
                  {avatar ? (
                    <img
                      src={avatar}
                      alt={`${user.firstName} ${user.lastName}`}
                      className="w-16 h-10 p-2 rounded-full object-cover border-2 shadow-sm transition-all duration-200 hover:scale-105 hover:ring-2 hover:ring-opacity-50"
                      style={
                        {
                          borderColor:
                            theme === "light" ? "#e5e7eb" : "#ffffff",
                          "--tw-ring-color":
                            theme === "light" ? colors.primary : "#60a5fa",
                        } as React.CSSProperties
                      }
                    />
                  ) : (
                    <div
                      className="w-14 h-8 rounded-full flex items-center justify-center text-sm font-medium border-2 shadow-sm transition-all duration-200 hover:scale-105 hover:ring-2 hover:ring-opacity-50"
                      style={
                        {
                          backgroundColor:
                            theme === "light" ? "#f3f4f6" : "#4b5563",
                          color: theme === "light" ? "#374151" : "#f9fafb",
                          borderColor:
                            theme === "light" ? "#d1d5db" : "#6b7280",
                          "--tw-ring-color":
                            theme === "light" ? colors.primary : "#60a5fa",
                        } as React.CSSProperties
                      }
                    >
                      {user.firstName.charAt(0)}
                      {user.lastName.charAt(0)}
                    </div>
                  )}
                </button>

                <AnimatePresence>
                  {isUserMenuOpen && (
                    <motion.div
                      initial={{ opacity: 0, y: 10, scale: 0.95 }}
                      animate={{ opacity: 1, y: 0, scale: 1 }}
                      exit={{ opacity: 0, y: 10, scale: 0.95 }}
                      transition={{ duration: 0.2 }}
                      className="absolute right-0 mt-2 w-56 rounded-lg shadow-lg border overflow-hidden"
                      style={{
                        backgroundColor: colors.background,
                        borderColor: theme === "light" ? "#e5e7eb" : "#374151",
                      }}
                    >
                      <div
                        className="p-4 border-b"
                        style={{
                          borderColor:
                            theme === "light" ? "#e5e7eb" : "#374151",
                        }}
                      >
                        <div
                          className="font-medium"
                          style={{ color: colors.text }}
                        >
                          {user.firstName} {user.lastName}
                        </div>
                        <div className="text-sm" style={{ color: colors.gray }}>
                          @{user.username}
                        </div>
                      </div>

                      <div className="py-2">
                        <Link
                          to={`/profile/${user.username}`}
                          className="flex items-center space-x-3 px-4 py-2 transition-colors hover:opacity-80 w-full text-left"
                          onClick={() => setIsUserMenuOpen(false)}
                          style={{ color: colors.text }}
                        >
                          <User size={16} />
                          <span>Profile</span>
                        </Link>

                        <Link
                          to="/settings"
                          className="flex items-center space-x-3 px-4 py-2 transition-colors hover:opacity-80 w-full text-left"
                          onClick={() => setIsUserMenuOpen(false)}
                          style={{ color: colors.text }}
                        >
                          <Settings size={16} />
                          <span>Settings</span>
                        </Link>

                        <Link
                          to="/help"
                          className="flex items-center space-x-3 px-4 py-2 transition-colors hover:opacity-80 w-full text-left"
                          onClick={() => setIsUserMenuOpen(false)}
                          style={{ color: colors.text }}
                        >
                          <HelpCircle size={16} />
                          <span>Help & Tutorial</span>
                        </Link>

                        <button
                          onClick={handleLogout}
                          className="flex items-center space-x-3 px-4 py-2 transition-colors hover:opacity-80 w-full text-left"
                          style={{ color: colors.text }}
                        >
                          <LogOut size={16} />
                          <span>Logout</span>
                        </button>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            ) : (
              <Link
                to="/login"
                className="px-4 py-2 rounded-lg font-medium transition-all duration-200 hover:scale-105 text-white hover:opacity-80"
                style={{ backgroundColor: colors.primary }}
              >
                <LogInIcon size={20} />
              </Link>
            )} */}

          </div>
        </div>
      </div>
    </motion.header>
  );
};

export default TopNavigation;
